import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ProductService } from '../../service/ProductService';
import { ResidenceService } from '../../service/ResidenceService';

const productService = new ProductService();
const residenceService = new ResidenceService();

const initialState = {
  residenceList: [],
  productList: [],
  productListCount: 0,
  status: 'idle',
  error: null
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const getProductsAsync = createAsyncThunk(
  'productList',
  async ({page = 0, first = 0, rows = 10, sortField = 'id', sortOrder = 1, filters = {}}, { rejectWithValue }) => {
    try {
      return await productService.getProducts(page, first, rows, sortField, sortOrder, filters);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar produtos');
    }
  }
);

export const getProductsCountAsync = createAsyncThunk(
  'productListCount',
  async ({filters = {}}, { rejectWithValue }) => {
    try {
      return await productService.getCountProducts(filters);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar contagem de produtos');
    }
  }
);

export const initialLoadAsync = createAsyncThunk(
  'productInitialLoad',
  async ({page = 0, first = 0, rows = 10, sortField = 'id', sortOrder = 1, filters = {}}, { rejectWithValue }) => {
    try {
      const residenceList = await residenceService.getResidences();
      const count = await productService.getCountProducts(filters);
      const list = await productService.getProducts(page, first, rows, sortField, sortOrder, filters);
      return {
        residenceList,
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao carregar dados iniciais');
    }
  }
);

export const deleteProductAsync = createAsyncThunk(
  'deleteProduct',
  async ({id, gridParameters}, { rejectWithValue }) => {
    try {
      await productService.deleteProduct(id);
      const count = await productService.getCountProducts(gridParameters.filters);
      const list = await productService.getProducts(0, 0, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao excluir produto');
    }
  }
);

export const editProductAsync = createAsyncThunk(
  'editProduct',
  async ({product, gridParameters}, { rejectWithValue }) => {
    try {
      await productService.editProduct(product);
      const count = await productService.getCountProducts(gridParameters.filters);
      const list = await productService.getProducts(gridParameters.page, gridParameters.first, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao editar produto');
    }
  }
);

export const createProductAsync = createAsyncThunk(
  'createProduct',
  async ({product, gridParameters}, { rejectWithValue }) => {
    try {
      await productService.createProduct(product);
      const count = await productService.getCountProducts(gridParameters.filters);
      const list = await productService.getProducts(0, 0, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao criar produto');
    }
  }
);

export const getResidencesAsync = createAsyncThunk(
  'residenceList',
  async (_, { rejectWithValue }) => {
    try {
      return await residenceService.getResidences();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar residências');
    }
  }
);

export const productSlice = createSlice({
  name: 'productSlice',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getResidencesAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getResidencesAsync.fulfilled, (state, action) => {
        state.residenceList = action.payload;
        state.error = null;
      })
      .addCase(getResidencesAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(initialLoadAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getProductsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getProductsCountAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(initialLoadAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.residenceList = action.payload.residenceList;
        state.productList = action.payload.list.data.map(d => {
          if (d.createdAt) {
            d.createdAtFormatted = new Date(d.createdAt).toLocaleDateString('pt-BR');
          }
          return d;
        });
        state.productListCount = action.payload.count;
        state.error = null;
      })
      .addCase(initialLoadAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(getProductsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.productList = action.payload.data.map(d => {
          if (d.createdAt) {
            d.createdAtFormatted = new Date(d.createdAt).toLocaleDateString('pt-BR');
          }
          return d;
        });
        state.error = null;
      })
      .addCase(getProductsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(getProductsCountAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.productListCount = action.payload;
        state.error = null;
      })
      .addCase(getProductsCountAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(createProductAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createProductAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.productList = action.payload.list.data;
        state.productListCount = action.payload.count;
        state.error = null;
      })
      .addCase(createProductAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(editProductAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(editProductAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.productList = action.payload.list.data;
        state.productListCount = action.payload.count;
        state.error = null;
      })
      .addCase(editProductAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(deleteProductAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteProductAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.productList = action.payload.list.data;
        state.productListCount = action.payload.count;
        state.error = null;
      })
      .addCase(deleteProductAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const selectResidenceList = (state) => state.product.residenceList;

export const selectProductList = (state) => state.product.productList;

export const selectProductListCount = (state) => state.product.productListCount;

export const selectLoading = (state) => state.product.status === 'loading';

export const selectError = (state) => state.product.error;

export default productSlice.reducer;